import React from "react";
import { DashboardPageLayout } from "../../../components/layout/DashboardPageLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { AppLink, LinkAnchor } from "../../../components/ui/link";
import {
  formatAdminTableDate,
  type AdminTableDateOptions,
} from "../../../lib/formatAdminTableDate";
import { AdminBusinessBillingForm } from "./AdminBusinessBillingForm";
import { AdminChatsSection } from "./AdminChatsSection";
import { AdminDeleteForm } from "./AdminDeleteForm";
import { AdminQuotesTable } from "./AdminQuotesTable";
import type { AdminOrganizationDetailPayload, AdminRouteMap } from "../types";

type AdminOrganizationDetailPageProps = {
  detail: AdminOrganizationDetailPayload;
  billingStatusOptions: readonly string[];
  dateOptions: AdminTableDateOptions;
  csrfToken: string;
  currentUserId: number | null;
  adminRoutes: AdminRouteMap;
};

export const AdminOrganizationDetailPage: React.FC<
  AdminOrganizationDetailPageProps
> = ({
  detail,
  billingStatusOptions,
  dateOptions,
  csrfToken,
  currentUserId,
  adminRoutes,
}) => {
  const { organization, business, quotes, chats, members, products, owner } =
    detail;
  const subtitleParts = [
    owner?.email ? `Owner: ${owner.email}` : null,
    `${members.length} member${members.length === 1 ? "" : "s"}`,
    `${detail.jobSiteCount} job site${detail.jobSiteCount === 1 ? "" : "s"}`,
    organization.stripeCustomerId
      ? `Stripe ${organization.stripeCustomerId}`
      : null,
  ].filter(Boolean);

  return (
    <DashboardPageLayout
      title={organization.name}
      subtitle={subtitleParts.join(" · ")}
      headerActions={
        <AppLink
          to={adminRoutes.adminOrganizations}
          variant="muted"
          className="text-sm"
        >
          ← All organizations
        </AppLink>
      }
    >
      <Card>
        <CardHeader>
          <CardTitle>Billing & trial</CardTitle>
          <CardDescription>
            Suite billing applies to all products. Updates sync to the public
            website record when one exists.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Trial started
              </p>
              <p className="text-sm mt-1 text-muted-foreground">
                {organization.trialStartedAt
                  ? formatAdminTableDate(
                      organization.trialStartedAt,
                      dateOptions,
                    )
                  : "—"}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Trial ends
              </p>
              <p className="text-sm mt-1 text-muted-foreground">
                {organization.trialEndsAt
                  ? formatAdminTableDate(organization.trialEndsAt, dateOptions)
                  : "—"}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                Suite billing status
              </p>
              <AdminBusinessBillingForm
                action={adminRoutes.adminUpdateOrganizationBilling.replace(
                  ":id",
                  String(organization.id),
                )}
                csrfToken={csrfToken}
                currentStatus={detail.suiteBillingStatus}
                statusOptions={billingStatusOptions}
              />
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
              Products
            </p>
            <dl className="grid gap-2 sm:grid-cols-3">
              {products.map((product) => (
                <div
                  key={product.key}
                  className="rounded-lg border border-border p-3"
                >
                  <dt className="text-xs text-muted-foreground capitalize">
                    {product.key.replace(/_/g, " ")}
                  </dt>
                  <dd className="text-sm font-medium capitalize mt-1">
                    {product.billingStatus.replace(/_/g, " ")}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Company profile</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Phone
            </p>
            <p className="text-sm mt-1">
              {organization.phone ?? (
                <span className="text-muted-foreground">—</span>
              )}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Trade
            </p>
            <p className="text-sm mt-1">
              {organization.tradeType ?? (
                <span className="text-muted-foreground">—</span>
              )}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Org created
            </p>
            <p className="text-sm mt-1 text-muted-foreground">
              {formatAdminTableDate(organization.createdAt, dateOptions)}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Website</CardTitle>
          <CardDescription>
            {business
              ? "Published contractor site and business billing mirror."
              : "No website yet — onboarding or assistant setup not finished."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {business ? (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Display name
                  </p>
                  <p className="text-sm font-medium mt-1">
                    {business.businessName ?? business.subdomain}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Subdomain
                  </p>
                  <p className="text-sm font-mono mt-1">{business.subdomain}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Business billing
                  </p>
                  <p className="text-sm mt-1 capitalize">
                    {business.billingStatus.replace(/_/g, " ")}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Business trial ends
                  </p>
                  <p className="text-sm mt-1 text-muted-foreground">
                    {business.trialEndsAt
                      ? formatAdminTableDate(business.trialEndsAt, dateOptions)
                      : "—"}
                  </p>
                </div>
              </div>
              <LinkAnchor
                variant="premium"
                className="text-sm"
                href={business.siteUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open live site
              </LinkAnchor>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              Billing and trial above still apply to this organization.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Team</CardTitle>
          <CardDescription>
            Members and roles in this organization.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 font-medium">Name</th>
                  <th className="text-left py-3 px-4 font-medium">Email</th>
                  <th className="text-left py-3 px-4 font-medium">Role</th>
                  <th className="text-left py-3 px-4 font-medium">Joined</th>
                  <th className="text-left py-3 px-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => {
                  const orgNames = member.deletionImpact.organizationsToDestroy
                    .map((o) => o.name)
                    .join(", ");
                  const organizationDestroyConfirmMessage = member
                    .deletionImpact.requiresOrganizationDestroy
                    ? `This user is the last owner or admin for ${orgNames}. Deleting them will permanently delete ${member.deletionImpact.organizationsToDestroy.length === 1 ? "that organization" : "those organizations"} (website, jobs, billing, and team data). This cannot be undone. Continue?`
                    : undefined;
                  return (
                    <tr
                      key={member.userId}
                      className="border-b border-border last:border-0"
                    >
                      <td className="py-3 px-4">
                        {member.name ?? (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4">{member.email}</td>
                      <td className="py-3 px-4 capitalize">{member.role}</td>
                      <td className="py-3 px-4 text-muted-foreground">
                        {member.joinedAt
                          ? formatAdminTableDate(member.joinedAt, dateOptions)
                          : "—"}
                      </td>
                      <td className="py-3 px-4">
                        {currentUserId != null &&
                        member.userId === currentUserId ? (
                          <span className="text-muted-foreground text-xs">
                            You
                          </span>
                        ) : member.platformAdmin ? (
                          <span className="text-muted-foreground text-xs">
                            Platform admin
                          </span>
                        ) : (
                          <AdminDeleteForm
                            action={adminRoutes.adminDestroyUser.replace(
                              ":id",
                              String(member.userId),
                            )}
                            csrfToken={csrfToken}
                            confirmMessage={`Delete user ${member.email}? This removes their login and may affect team access.`}
                            organizationDestroyConfirmMessage={
                              organizationDestroyConfirmMessage
                            }
                            label="Delete user"
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Quotes</CardTitle>
          <CardDescription>
            {business
              ? `Quote requests for this website (${quotes.length} shown).`
              : "Quotes appear once a website exists."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!business || quotes.length === 0 ? (
            <p className="text-sm text-muted-foreground">No quotes yet.</p>
          ) : (
            <AdminQuotesTable
              quotes={quotes}
              dateOptions={dateOptions}
              csrfToken={csrfToken}
              adminRoutes={adminRoutes}
              showBusinessColumn={false}
            />
          )}
        </CardContent>
      </Card>

      <AdminChatsSection
        chats={chats}
        dateOptions={dateOptions}
        csrfToken={csrfToken}
        adminRoutes={adminRoutes}
        accountScoped
      />
    </DashboardPageLayout>
  );
};
